import React, { useMemo, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { KeyRound, Plus } from 'lucide-react';
import { useKeyHolderNames, useKeyRegister } from '../../hooks/keyRegister';
import { useBuildingsData } from '../../Helpers/BuildingSummariesContext';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { nextSerialStart, typeObjectLinks } from '../../Helpers/keyRegisterUtils';
import { KeyHandOutInput, KeyType, RegisterKey } from '../../types/keyRegister';
import ConfirmationModal from '../../Components/ConfirmationModal';
import Skeleton from '../../Components/Skeleton';
import KeySummaryCards from './KeySummaryCards';
import KeyRegisterToolbar, { KeyFilterChip } from './KeyRegisterToolbar';
import KeyFilterModal from './KeyFilterModal';
import KeyTypeSection from './KeyTypeSection';
import KeyTypeModal, { BuildingOption, KeyTypeSubmission, toBuildingOptions } from './KeyTypeModal';
import AddKeysModal from './AddKeysModal';
import KeyModal from './KeyModal';
import HandOutKeysModal from './HandOutKeysModal';
import LockScheduleModal from './LockScheduleModal';
import { useKeyRegisterView } from './useKeyRegisterView';
import { objectLinksLabel } from './keyObjects';
import { EMPTY_SELECTION, isSelectionEmpty, KeySelection, pickLabel } from './keySelection';
import { BreadcrumbComponent } from '../../Components/BreadCrumb';
import './KeyRegister.css';

const NO_KEYS: RegisterKey[] = [];

/**
 * Nyckelregister at /keys (admin-only): status cards that filter, then one collapsible section per
 * key type listing its keys. The Filter button's tree (`?in=`, see keySelection.ts) narrows both to
 * buildings, their building-wide keys and single lägenheter, lokaler and förråd; the building page
 * (`?building=`) and an object's Nycklar button (`&object=`) still link here. A row opens the key modal
 * (`?key={keyKey}`), where single events are recorded; "Lämna ut nycklar" hands out several at once. The last card opens the Låsschema files. Spec: workspace docs/specs/key-register.md, section "Web".
 */
const KeyRegisterPage: React.FC = () => {
    useDocumentTitle('Nycklar');
    const register = useKeyRegister();
    const { types, keys, typesByKey, keysByType, today, loading, error, submitting } = register;
    // Covers every holder and log actor of every key, so the key modal reuses it.
    const { holderName, actorName } = useKeyHolderNames(keys);
    const { buildings } = useBuildingsData();
    const buildingOptions = useMemo<BuildingOption[]>(() => toBuildingOptions(buildings), [buildings]);
    const allBuildingKeys = useMemo(() => buildingOptions.map(b => b.key), [buildingOptions]);
    const view = useKeyRegisterView({ types, keys, typesByKey, keysByType, today, holderName, allBuildingKeys });

    const [typeModal, setTypeModal] = useState<{ type: KeyType | null } | null>(null);
    const [addKeysFor, setAddKeysFor] = useState<KeyType | null>(null);
    const [deleteFor, setDeleteFor] = useState<KeyType | null>(null);
    const [archiveFor, setArchiveFor] = useState<KeyType | null>(null);
    const [showHandOut, setShowHandOut] = useState(false);
    const [showLockSchedules, setShowLockSchedules] = useState(false);
    // Row actions (archive, delete) have no form of their own to show a failure in; the modals show theirs.
    const [pageError, setPageError] = useState<string | null>(null);
    const [showFilters, setShowFilters] = useState(false);
    const [filterOpenCount, setFilterOpenCount] = useState(0);

    const buildingNames = useMemo(() => {
        const names: Record<string, string> = {};
        for (const b of buildingOptions) names[b.key] = b.name;
        return names;
    }, [buildingOptions]);
    const buildingNamesOf = (type: KeyType): string[] =>
        Object.keys(type.opensBuildings ?? {}).map(k => buildingNames[k]).filter((n): n is string => !!n);

    const { objectsByBuilding, selection, options } = view;
    const linkedObjectKeys = useMemo(
        () => new Set(types.flatMap(type => typeObjectLinks(type).map(link => link.objectKey))),
        [types]
    );

    // One chip per picked building (in the tree's order), then the scope toggles.
    const filterChips: KeyFilterChip[] = [];
    const pickedBuildings = Object.keys(selection).sort((a, b) =>
        (buildingNames[a] ?? '').localeCompare(buildingNames[b] ?? '', 'sv', { numeric: true }));
    for (const buildingKey of pickedBuildings) {
        const withoutIt: KeySelection = { ...selection };
        delete withoutIt[buildingKey];
        filterChips.push({
            id: `in-${buildingKey}`,
            label: `${buildingNames[buildingKey] ?? 'Okänd byggnad'}: ${pickLabel(selection[buildingKey], objectsByBuilding[buildingKey] ?? [])}`,
            onRemove: () => view.setSelection(withoutIt),
        });
    }
    if (options.showScrapped) filterChips.push({ id: 'scrapped', label: 'Kasserade nycklar', onRemove: () => view.setOptions({ ...options, showScrapped: false }) });
    if (options.showArchived) filterChips.push({ id: 'archived', label: 'Arkiverade nyckeltyper', onRemove: () => view.setOptions({ ...options, showArchived: false }) });

    const openFilters = () => {
        view.loadAllObjects();
        setFilterOpenCount(n => n + 1);
        setShowFilters(true);
    };

    const selectedKey = view.selectedKeyKey ? keys.find(k => k.key === view.selectedKeyKey) ?? null : null;
    const selectedType = selectedKey ? typesByKey[selectedKey.typeKey] ?? null : null;

    const runRowAction = async (action: () => Promise<unknown>) => {
        setPageError(null);
        try {
            await action();
        } catch (err) {
            setPageError(err instanceof Error ? err.message : 'Något gick fel');
        }
    };

    /**
     * Saves the type, then its file changes. A file failure closes the modal and shows on the page, so
     * a retry edits the saved type instead of creating it again.
     */
    const saveType = async ({ input, filesToUpload, attachmentKeysToRemove }: KeyTypeSubmission) => {
        let typeKey: string;
        if (typeModal?.type) {
            typeKey = typeModal.type.key;
            await register.updateType(typeKey, input);
        } else {
            typeKey = (await register.createType(input)).key;
        }
        setTypeModal(null);
        if (filesToUpload.length === 0 && attachmentKeysToRemove.length === 0) return;
        await runRowAction(async () => {
            try {
                await register.saveTypeAttachments(typeKey, filesToUpload, attachmentKeysToRemove);
            } catch (err) {
                throw new Error(`Nyckeltypen sparades, men inte alla filer: ${err instanceof Error ? err.message : 'okänt fel'}`);
            }
        });
    };

    const addKeys = async (count: number, startNumber: number) => {
        if (!addKeysFor) return;
        await register.addKeys(addKeysFor.key, count, startNumber);
        setAddKeysFor(null);
    };

    const handOutKeys = async (keyKeys: string[], input: KeyHandOutInput) => {
        await register.handOutKeys(keyKeys, input);
        setShowHandOut(false);
    };

    const confirmDelete = async () => {
        if (!deleteFor) return;
        const type = deleteFor;
        setDeleteFor(null);
        await runRowAction(() => register.deleteType(type.key));
    };

    const confirmArchive = async () => {
        if (!archiveFor) return;
        const type = archiveFor;
        setArchiveFor(null);
        await runRowAction(() => register.archiveType(type.key));
    };

    const newTypeButton = (
        <Button variant="primary" className="key-register-new" onClick={() => setTypeModal({ type: null })}>
            <Plus size={18} className="me-1" aria-hidden="true" />Ny nyckeltyp
        </Button>
    );

    const renderSections = () => {
        if (loading && types.length === 0) {
            return (
                <div className="key-sections" aria-busy="true">
                    {[0, 1, 2].map(i => (
                        <div key={i} className="key-section key-section--skeleton">
                            <Skeleton width="50%" /><Skeleton width="30%" height="0.85em" />
                        </div>
                    ))}
                </div>
            );
        }
        if (types.length === 0) {
            return (
                <div className="key-register-empty">
                    <p>Inga nyckeltyper ännu. Skapa en nyckeltyp och lägg sedan till exemplar.</p>
                    {newTypeButton}
                </div>
            );
        }
        if (view.sections.length === 0) {
            return (
                <div className="key-register-empty">
                    <p>
                        {view.filtering
                            ? (view.query.trim() ? 'Inga nycklar matchar sökningen' : 'Inga nycklar matchar filtret')
                            : !isSelectionEmpty(selection) ? 'Inga nyckeltyper är kopplade till urvalet.' : 'Alla nyckeltyper är arkiverade.'}
                    </p>
                    {view.filtering
                        ? <Button variant="link" className="p-0" onClick={view.clearFilters}>Rensa filter</Button>
                        : !isSelectionEmpty(selection) && <Button variant="link" className="p-0" onClick={() => view.setSelection(EMPTY_SELECTION)}>Visa alla byggnader</Button>}
                </div>
            );
        }
        return (
            <div className="key-sections">
                {view.sections.map(section => {
                    const { type } = section;
                    return (
                        <KeyTypeSection
                            key={type.key}
                            section={section}
                            hasAnyKeys={(keysByType[type.key] ?? NO_KEYS).length > 0}
                            buildingNames={buildingNamesOf(type)}
                            objectsLabel={objectLinksLabel(type, objectsByBuilding, k => buildingNames[k])}
                            expanded={view.isExpanded(type.key)}
                            today={today}
                            disabled={submitting}
                            holderName={holderName}
                            getFileUrl={register.getTypeAttachmentUrl}
                            onToggle={() => view.toggleExpanded(type.key)}
                            onSelectKey={view.selectKey}
                            onEdit={() => setTypeModal({ type })}
                            onAddKeys={() => setAddKeysFor(type)}
                            onToggleArchive={() => (type.archived ? runRowAction(() => register.unarchiveType(type.key)) : setArchiveFor(type))}
                            onDelete={() => setDeleteFor(type)}
                        />
                    );
                })}
            </div>
        );
    };

    return (
        <>
        <BreadcrumbComponent items={[{ text: 'Nycklar' }]} />
        <div className="key-register-page">
            <header className="key-register-header">
                <div>
                    <h1 className="key-register-title page-title">Nycklar</h1>
                    <p className="key-register-subtitle">Aktuell status för alla fysiska nycklar</p>
                </div>
                <div className="key-register-actions">
                    {types.length > 0 && (
                        <Button variant="outline-primary" className="key-register-new" onClick={() => setShowHandOut(true)}>
                            <KeyRound size={18} className="me-1" aria-hidden="true" />Lämna ut nycklar
                        </Button>
                    )}
                    {newTypeButton}
                </div>
            </header>

            {error && <Alert variant="danger">{error}</Alert>}
            {pageError && <Alert variant="danger" dismissible onClose={() => setPageError(null)}>{pageError}</Alert>}

            <KeySummaryCards
                summary={view.summary}
                active={view.statusFilter}
                onToggle={view.toggleStatusFilter}
                lockScheduleCount={register.lockSchedules.length}
                onOpenLockSchedules={() => setShowLockSchedules(true)}
            />

            <div className="key-table-card">
                <KeyRegisterToolbar
                    statusFilter={view.statusFilter}
                    query={view.query}
                    onQueryChange={view.setQuery}
                    filterCount={filterChips.length}
                    onOpenFilters={openFilters}
                    chips={filterChips}
                />
                {renderSections()}
            </div>

            <KeyFilterModal
                show={showFilters}
                openCount={filterOpenCount}
                onHide={() => setShowFilters(false)}
                buildings={buildingOptions}
                objectsByBuilding={objectsByBuilding}
                loadingBuildings={view.loadingBuildings}
                linkedObjectKeys={linkedObjectKeys}
                types={types}
                keysByType={keysByType}
                selection={selection}
                options={options}
                onApply={(nextSelection, nextOptions) => {
                    view.setSelection(nextSelection);
                    view.setOptions(nextOptions);
                    setShowFilters(false);
                }}
            />

            <KeyTypeModal
                show={!!typeModal}
                onHide={() => setTypeModal(null)}
                type={typeModal?.type}
                buildings={buildingOptions}
                onSubmit={saveType}
                submitting={submitting}
            />

            <AddKeysModal
                show={!!addKeysFor}
                onHide={() => setAddKeysFor(null)}
                type={addKeysFor}
                defaultStart={addKeysFor ? nextSerialStart(keysByType[addKeysFor.key] ?? NO_KEYS) : 1}
                onSubmit={addKeys}
                submitting={submitting}
            />

            <HandOutKeysModal
                show={showHandOut}
                onHide={() => setShowHandOut(false)}
                types={types}
                keysByType={keysByType}
                today={today}
                onSubmit={handOutKeys}
                submitting={submitting}
            />

            <LockScheduleModal
                show={showLockSchedules}
                onHide={() => setShowLockSchedules(false)}
                files={register.lockSchedules}
                getUrl={register.getTypeAttachmentUrl}
                onAdd={register.addLockSchedules}
                onRemove={register.removeLockSchedule}
            />

            <ConfirmationModal
                show={!!deleteFor}
                title="Ta bort nyckeltyp"
                message={`Nyckeltypen "${deleteFor?.name ?? ''}" tas bort. Det går inte att ångra.`}
                confirmText="Ta bort nyckeltypen"
                variant="danger"
                loading={submitting}
                onConfirm={confirmDelete}
                onCancel={() => setDeleteFor(null)}
            />

            <ConfirmationModal
                show={!!archiveFor}
                title="Arkivera nyckeltyp"
                message={`Nyckeltypen "${archiveFor?.name ?? ''}" och dess exemplar döljs i registret. Du hittar den under Filter, Arkiverade nyckeltyper, och kan återställa den där.`}
                confirmText="Arkivera nyckeltypen"
                variant="warning"
                loading={submitting}
                onConfirm={confirmArchive}
                onCancel={() => setArchiveFor(null)}
            />

            <KeyModal
                registerKey={selectedKey}
                type={selectedType}
                today={today}
                submitting={submitting}
                holderName={holderName}
                actorName={actorName}
                onHide={() => view.selectKey(null)}
                updateKeyFields={register.updateKeyFields}
                recordEvent={register.recordEvent}
                getReceiptUrl={register.getReceiptUrl}
            />
        </div>
        </>
    );
};

export default KeyRegisterPage;
